/**
 * ACTIVITY-CORE — helpers puros de presentación del feed (agnósticos de UI).
 * Los textos humanos (i18n) viven en la app; aquí solo estructura:
 * parseo del patch, agrupación por batch y detección de "hecho offline".
 */

/** Fila de activity_recent tal y como llega del SQLite local. */
export interface ActivityRow {
  id: string;
  scope_id: string | null;
  branch_id: string | null;
  register_session_id: string | null;
  entity_type: string | null;
  entity_id: string | null;
  action: string | null;
  actor_user_id: string | null;
  actor_name: string | null;
  actor_device_id: string | null;
  platform: string | null;
  server_ts: string | null;
  client_ts: string | null;
  patch: string | null;
  batch_id: string | null;
  reverts_activity_id: string | null;
}

export type ActivityAction = "created" | "updated" | "deleted" | "restored" | "reverted";

export interface ActivityFieldChange {
  old: unknown;
  new: unknown;
}

export type ActivityPatch = Record<string, ActivityFieldChange>;

/** Type guard de un cambio `{old, new}` (sin aserciones inseguras). */
function isFieldChange(value: unknown): value is { old?: unknown; new?: unknown } {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/** Parsea el patch jsonb (viene como TEXT en SQLite). Nunca lanza. */
export function parseActivityPatch(raw: string | null | undefined): ActivityPatch {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const patch: ActivityPatch = {};
    for (const [field, change] of Object.entries(parsed)) {
      if (isFieldChange(change)) {
        patch[field] = { old: change.old, new: change.new };
      }
    }
    return patch;
  } catch {
    return {};
  }
}

/** Campos cambiados de una entrada, en orden estable. */
export function changedFields(patch: ActivityPatch): string[] {
  return Object.keys(patch).toSorted();
}

/**
 * Umbral a partir del cual una diferencia client_ts↔server_ts se considera
 * "hecho offline y sincronizado después" (y no mera latencia de red).
 */
export const OFFLINE_LAG_THRESHOLD_MS = 60_000;

/**
 * Milisegundos entre el momento en que el cliente hizo el cambio (client_ts)
 * y su llegada al servidor (server_ts). `null` si falta alguno o no parsea.
 */
export function activityLagMs(row: Pick<ActivityRow, "server_ts" | "client_ts">): number | null {
  if (!row.server_ts || !row.client_ts) {
    return null;
  }
  const server = Date.parse(row.server_ts);
  const client = Date.parse(row.client_ts);
  if (Number.isNaN(server) || Number.isNaN(client)) {
    return null;
  }
  return server - client;
}

/** ¿Se hizo offline? (el cliente lo estampó mucho antes de que llegara al servidor) */
export function isOfflineEdit(
  row: Pick<ActivityRow, "server_ts" | "client_ts">,
  thresholdMs: number = OFFLINE_LAG_THRESHOLD_MS,
): boolean {
  const lag = activityLagMs(row);
  return lag !== null && lag > thresholdMs;
}

export interface ActivityBatchGroup<T extends ActivityRow = ActivityRow> {
  /** batch_id común (o el id de la fila si no tiene batch). */
  batchId: string;
  entries: T[];
}

/**
 * Agrupa filas CONSECUTIVAS del feed (ya ordenado server_ts DESC) por batch_id:
 * "importó 40 productos" se pinta como un grupo, no 40 tarjetas. No reordena.
 */
export function groupActivityByBatch<T extends ActivityRow>(
  rows: readonly T[],
): ActivityBatchGroup<T>[] {
  const groups: ActivityBatchGroup<T>[] = [];
  for (const row of rows) {
    const key = row.batch_id ?? row.id;
    const last = groups.at(-1);
    if (last && last.batchId === key) {
      last.entries.push(row);
    } else {
      groups.push({ batchId: key, entries: [row] });
    }
  }
  return groups;
}

// ─── Resumen del turno (cuadre de caja) ──────────────────────────────────────

export interface ShiftActivitySummary {
  /** Nº de anulaciones (órdenes/líneas marcadas void o soft-deleted). */
  voids: number;
  /** Nº de descuentos aplicados/cambiados. */
  discounts: number;
  /** Nº de reaperturas de orden (paid/void → open). */
  reopens: number;
  /** Nº total de entradas del turno. */
  total: number;
}

/** ¿La entrada representa una anulación? (orden a void, o línea anulada). */
export function isVoidEntry(row: ActivityRow): boolean {
  const patch = parseActivityPatch(row.patch);
  if (row.entity_type === "order" && patch.status?.new === "void") {
    return true;
  }
  return row.entity_type === "order_line" && row.action === "deleted";
}

/** ¿La entrada representa un descuento aplicado o modificado (a valor > 0)? */
export function isDiscountEntry(row: ActivityRow): boolean {
  if (row.entity_type !== "order") {
    return false;
  }
  const change = parseActivityPatch(row.patch).discount;
  return Boolean(change) && typeof change?.new === "number" && change.new > 0;
}

/** ¿La entrada representa una reapertura (paid/void → open)? */
export function isReopenEntry(row: ActivityRow): boolean {
  if (row.entity_type !== "order") {
    return false;
  }
  const { status } = parseActivityPatch(row.patch);
  return Boolean(
    status && status.new === "open" && (status.old === "paid" || status.old === "void"),
  );
}

/**
 * Resumen antifraude de la actividad de un turno (para el cuadre de caja):
 * cuenta anulaciones, descuentos y reaperturas sobre las filas YA filtradas
 * por register_session_id.
 */
export function summarizeShiftActivity(rows: readonly ActivityRow[]): ShiftActivitySummary {
  let voids = 0;
  let discounts = 0;
  let reopens = 0;
  for (const row of rows) {
    if (isVoidEntry(row)) {
      voids += 1;
    }
    if (isDiscountEntry(row)) {
      discounts += 1;
    }
    if (isReopenEntry(row)) {
      reopens += 1;
    }
  }
  return { voids, discounts, reopens, total: rows.length };
}
